import React, { ChangeEvent, useState } from "react"

import { ReactComponent as IconChevronDown } from "@common_assets/svg/chevron-down.svg"
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material"
import { DateValidationError } from "@mui/x-date-pickers/models"
import DatePicker from "components/common/datePicker/DatePicker"
import { getDatePickerErrorMessage } from "components/module/admin/schoolTransactions/datePicker/validaiton"
import { Dayjs } from "dayjs"
import { userIsFreeDemoSchoolPlan } from "hooks/schoolPlans"
import { useTranslation } from "react-i18next"
import { IClassWithGroups } from "ts/interfaces/Transaction"
import { SenderType } from "ts/types/SenderType"

import { TypeTransactions } from "../AllTransactionsTeacher"
import {
  getClassCheckState,
  IClassTreeSelection,
  typeTransactions,
} from "./PopoverContent.utility"

const EllipsisLabel = ({
  name,
  maxWidth,
  variant = "body1",
}: {
  name: string
  maxWidth: string
  variant?: "body1" | "body2"
}) => (
  <Tooltip title={name} placement="right" arrow={true}>
    <Typography
      fontWeight="500"
      variant={variant}
      sx={{
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        maxWidth,
      }}
    >
      {name}
    </Typography>
  </Tooltip>
)

interface IPopoverContent {
  typeTransactionsArray: TypeTransactions[]
  handleCheckboxTransactions: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void
  handleToggleClass: (classItem: IClassWithGroups) => void
  handleToggleGroup: (classItem: IClassWithGroups, groupId: number) => void
  onApply: (errorFrom: boolean, errorTo: boolean) => void
  onReset: () => void
  selectedDateFrom: Dayjs | null
  selectedDateTo: Dayjs | null
  handleDateFrom: (data: Dayjs | null) => void
  handleDateTo: (data: Dayjs | null) => void
  classes: IClassWithGroups[]
  isLoadingClasses: boolean
  classSelection: IClassTreeSelection
  isLoading: boolean
  getSenderType: (event: SenderType) => void
  senderType: SenderType | ""
}

const PopoverContent = ({
  typeTransactionsArray,
  handleCheckboxTransactions,
  onApply,
  onReset,
  handleDateFrom,
  handleDateTo,
  selectedDateFrom,
  selectedDateTo,
  classes,
  isLoadingClasses,
  classSelection,
  handleToggleClass,
  handleToggleGroup,
  isLoading,
  getSenderType,
  senderType,
}: IPopoverContent) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isFreeDemo = userIsFreeDemoSchoolPlan()

  const [expandedClassIds, setExpandedClassIds] = useState<number[]>(() =>
    classes
      .filter(
        (classItem) =>
          getClassCheckState(classSelection, classItem) === "indeterminate"
      )
      .map(({ id }) => id)
  )
  const [isErrorFrom, setIsErrorFrom] = useState(false)
  const [isErrorTo, setIsErrorTo] = useState(false)
  const [errorMessageFrom, setErrorMessageFrom] = useState("")
  const [errorMessageTo, setErrorMessageTo] = useState("")

  const handleSelect = (event: ChangeEvent<HTMLInputElement>) => {
    getSenderType(event.target.value as SenderType)
  }

  const toggleExpanded = (classId: number) => {
    setExpandedClassIds((prev) =>
      prev.includes(classId)
        ? prev.filter((id) => id !== classId)
        : [...prev, classId]
    )
  }

  return (
    <Stack bgcolor="common.white" p={4}>
      <Stack direction="row" columnGap={2} flexWrap="wrap" mb={2}>
        <Box flex="1" mx={2} maxWidth="220px">
          <Typography variant="body1" sx={{ mb: "14px" }}>
            {t("allTransactions.class")}
          </Typography>
          <Box
            sx={{
              height: "215px",
              overflow: "auto",
              pl: 1,
            }}
            className="smooth-scrollbar"
          >
            <FormGroup
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              {isLoadingClasses && <CircularProgress size="24px" />}
              {classes.map((classItem) => {
                const checkState = getClassCheckState(classSelection, classItem)
                const groups = classItem.groups ?? []
                const isExpanded =
                  !isFreeDemo && expandedClassIds.includes(classItem.id)
                const groupsId = `all-transactions-class-groups-${classItem.id}`

                return (
                  <Box key={classItem.id} mb={2}>
                    <Stack direction="row" alignItems="center">
                      <FormControlLabel
                        sx={{ mr: 0 }}
                        control={
                          <Checkbox
                            checked={checkState === "checked"}
                            indeterminate={checkState === "indeterminate"}
                            onChange={() => handleToggleClass(classItem)}
                          />
                        }
                        label={
                          <EllipsisLabel
                            name={classItem.name}
                            maxWidth={isFreeDemo ? "170px" : "130px"}
                          />
                        }
                      />
                      {!isFreeDemo && (
                        <IconButton
                          size="small"
                          sx={{ ml: "auto" }}
                          aria-expanded={isExpanded}
                          aria-controls={isExpanded ? groupsId : undefined}
                          aria-label={t(
                            isExpanded
                              ? "allTransactions.hideGroups"
                              : "allTransactions.showGroups",
                            { name: classItem.name }
                          )}
                          onClick={() => toggleExpanded(classItem.id)}
                        >
                          <IconChevronDown
                            style={{
                              width: "16px",
                              height: "16px",
                              transform: isExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                              color: theme.palette.primary.main,
                            }}
                          />
                        </IconButton>
                      )}
                    </Stack>
                    {isExpanded && (
                      <Box id={groupsId} pl={2} mt={1}>
                        {groups.length ? (
                          <>
                            <Typography
                              variant="body2"
                              fontWeight="700"
                              component="p"
                              mb={1}
                            >
                              {t("allTransactions.groups")}
                            </Typography>
                            {groups.map((group) => (
                              <FormControlLabel
                                key={group.id}
                                sx={{ display: "flex", mb: 1 }}
                                control={
                                  <Checkbox
                                    checked={
                                      checkState === "checked" ||
                                      classSelection.groupIds.includes(group.id)
                                    }
                                    onChange={() =>
                                      handleToggleGroup(classItem, group.id)
                                    }
                                  />
                                }
                                label={
                                  <EllipsisLabel
                                    name={group.name}
                                    maxWidth="150px"
                                    variant="body2"
                                  />
                                }
                              />
                            ))}
                          </>
                        ) : (
                          <Typography
                            variant="body3"
                            component="p"
                            sx={{
                              bgcolor: "mockup.neutral95",
                              color: "mockup.neutral10",
                              borderRadius: "8px",
                              p: 1,
                            }}
                          >
                            {t("allTransactions.noGroupsInClass")}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                )
              })}
            </FormGroup>
          </Box>
        </Box>
        <Box flex="1" mx={2}>
          <Typography variant="body1" mb="14px">
            {t("allTransactions.typeOfTransaction")}
          </Typography>
          <FormGroup>
            {typeTransactions.map((type, index) => (
              <FormControlLabel
                sx={{ mb: 2 }}
                key={index}
                control={
                  <Checkbox
                    checked={typeTransactionsArray.includes(type.toLowerCase())}
                    name={type}
                    onChange={handleCheckboxTransactions}
                  />
                }
                label={
                  <Typography fontWeight="500" variant="body1">
                    {type}
                  </Typography>
                }
              />
            ))}
          </FormGroup>
        </Box>
      </Stack>
      <Box mx={2}>
        <Typography variant="body1" mb="20px">
          {t("allTransactions.dateRange")}
        </Typography>
        <Box display="flex" columnGap={1}>
          <DatePicker
            label={t("allTransactions.dateFrom")}
            value={selectedDateFrom}
            onChange={handleDateFrom}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  "& .MuiSvgIcon-root": {
                    fontSize: "20px",
                  },
                },
                error: !!errorMessageFrom,
                helperText: errorMessageFrom,
              },
            }}
            onError={(error: DateValidationError) => {
              const message = getDatePickerErrorMessage(
                error,
                "start",
                selectedDateFrom,
                undefined,
                selectedDateTo
              )
              setErrorMessageFrom(message)
              setIsErrorFrom(!!error)
            }}
          />
          <DatePicker
            label={t("allTransactions.dateTo")}
            value={selectedDateTo}
            onChange={handleDateTo}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  "& .MuiSvgIcon-root": {
                    fontSize: "20px",
                  },
                },
                error: !!errorMessageTo,
                helperText: errorMessageTo,
              },
            }}
            onError={(error: DateValidationError) => {
              const message = getDatePickerErrorMessage(
                error,
                "end",
                selectedDateTo,
                selectedDateFrom,
                undefined
              )
              setErrorMessageTo(message)
              setIsErrorTo(!!error)
            }}
          />
        </Box>
      </Box>
      <Box mt={3} mx={2}>
        <TextField
          select
          onChange={handleSelect}
          value={senderType}
          label="Select Sender"
        >
          <MenuItem value="" disableRipple>
            <Typography fontWeight="500">None</Typography>
          </MenuItem>
          <MenuItem value="teacher" disableRipple>
            <Typography fontWeight="500">Teacher</Typography>
          </MenuItem>
          <MenuItem value="helper" disableRipple>
            <Typography fontWeight="500">Helper</Typography>
          </MenuItem>
        </TextField>
      </Box>

      <Box display="flex" width="100%" justifyContent="flex-end" py="32px">
        <Button variant="text" onClick={onReset}>
          {t("allTransactions.resetFilters")}
        </Button>
        <Button
          disabled={isLoading}
          onClick={() => onApply(isErrorFrom, isErrorTo)}
        >
          {isLoading && (
            <span style={{ marginRight: "3px" }}>
              <CircularProgress size="12px" color="inherit" />
            </span>
          )}
          {t("allTransactions.apply")}
        </Button>
      </Box>
    </Stack>
  )
}
export default PopoverContent
